<template>
  <div class="sx">
    <header class="sx-head">
      <span class="sx-eyebrow">Paramètres</span>
      <h1 class="sx-title">Prestataires</h1>
      <p class="sx-sub">
        Configuration des prestataires de livraison : à l'expédition d'un bon d'envoi, ses colis sont créés automatiquement
        chez le prestataire choisi via son API.
      </p>
    </header>

    <SettingsNav />

    <section class="sx-card" aria-labelledby="dp-list-title">
      <div class="sx-list-head">
        <h2 id="dp-list-title">Prestataires <span class="sx-count">{{ providers.length }}</span></h2>
        <div class="dp-head-actions">
          <div class="sx-search">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input v-model="search" type="search" placeholder="Rechercher un prestataire…" aria-label="Rechercher un prestataire" />
          </div>
        </div>
      </div>

      <div v-if="loading">
        <span v-for="n in 3" :key="n" class="sx-skel"></span>
      </div>
      <div v-else-if="loadError" class="sx-load-error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        Impossible de charger les prestataires.
        <button type="button" class="btn btn-sm btn-light" @click="fetchProviders">Réessayer</button>
      </div>
      <div v-else-if="!filtered.length" class="sx-empty">
        <i class="ph ph-plugs-connected" aria-hidden="true"></i>
        <p>{{ search ? "Aucun prestataire ne correspond à votre recherche." : "Aucun prestataire configuré pour le moment." }}</p>
      </div>
      <ul v-else class="sx-rows">
        <li v-for="item in filtered" :key="item.id" class="sx-row" :class="{ 'is-new': item.id === lastSavedId }">
          <span class="sx-avatar" aria-hidden="true">{{ initials(item.name) }}</span>
          <div class="sx-main">
            <strong>{{ item.name }}</strong>
            <small>
              <span v-if="item.code" class="sx-code">{{ item.code }}</span>
              <span v-if="item.api_base_url" class="dp-url">{{ item.api_base_url }}</span>
            </small>
          </div>
          <div class="sx-meta">
            <span class="sx-tag" :class="item.active ? 'tone-success' : 'tone-muted'">
              <i class="ph" :class="item.active ? 'ph-check-circle' : 'ph-pause-circle'" aria-hidden="true"></i>
              {{ item.active ? "Actif" : "Inactif" }}
            </span>
            <span v-if="item.user_id" class="sx-tag tone-driver" title="Livreur établi comme prestataire (depuis la liste des utilisateurs)">
              <i class="ph ph-moped" aria-hidden="true"></i>Livreur{{ item.driver_name && item.driver_name !== item.name ? ` · ${item.driver_name}` : "" }}
            </span>
            <span v-if="!item.user_id" class="sx-tag tone-warning" title="Lier un livreur pour que ce prestataire soit proposé à l'expédition">
              <i class="ph ph-link-simple-break" aria-hidden="true"></i>Aucun livreur lié
            </span>
            <span v-if="!item.api_configured" class="sx-tag tone-warning" title="Renseigner l'URL de l'API et l'endpoint pour pouvoir l'utiliser à l'expédition">
              <i class="ph ph-plugs" aria-hidden="true"></i>API non configurée
            </span>
            <span class="sx-tag tone-info"><i class="ph ph-key" aria-hidden="true"></i>{{ authLabel(item.api_auth_type) }}</span>
            <span class="sx-tag"><i class="ph ph-paper-plane-tilt" aria-hidden="true"></i><b>{{ Number(item.bons) || 0 }}</b> bon(s)</span>
          </div>
          <div class="sx-actions">
            <button
              type="button"
              class="sx-icon-btn"
              :aria-label="item.active ? `Désactiver ${item.name}` : `Activer ${item.name}`"
              :title="item.active ? 'Désactiver' : 'Activer'"
              @click="toggle(item)"
            >
              <i class="ph" :class="item.active ? 'ph-toggle-right' : 'ph-toggle-left'" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="sx-icon-btn"
              :aria-label="item.user_id ? `Dissocier ${item.name} de son livreur` : `Lier ${item.name} à un livreur`"
              :title="item.user_id ? 'Dissocier du livreur' : 'Lier à un livreur'"
              @click="item.user_id ? unlink(item) : linkDriver(item)"
            >
              <i class="ph" :class="item.user_id ? 'ph-link-break' : 'ph-link'" aria-hidden="true"></i>
            </button>
            <button type="button" class="sx-icon-btn" :aria-label="`Modifier ${item.name}`" title="Modifier" @click="openForm(item)">
              <i class="ph ph-pencil-simple" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="sx-icon-btn is-danger"
              :aria-label="`Supprimer ${item.name}`"
              :title="Number(item.bons) > 0 ? 'Impossible : prestataire utilisé par des bons' : 'Supprimer'"
              :disabled="Number(item.bons) > 0"
              @click="remove(item)"
            >
              <i class="ph ph-trash" aria-hidden="true"></i>
            </button>
          </div>
        </li>
      </ul>
    </section>

    <DeliveryProviderFormModal ref="formModal" @saved="onSaved" />
  </div>
</template>

<script lang="ts">
import SettingsNav from "./SettingsNav.vue";
import DeliveryProviderFormModal from "./DeliveryProviderFormModal.vue";
import { computed, defineComponent, onMounted, ref } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";
import { pickWithSelect2 } from "@/utils/swalSelect2";

interface Provider {
  id: number;
  name: string;
  code: string | null;
  active: boolean;
  api_base_url: string | null;
  api_auth_type: string;
  bons?: number;
  [key: string]: any;
}

const authOptions = [
  { value: "none", label: "Aucune", help: "L'API ne demande pas d'authentification." },
  { value: "bearer", label: "Bearer token", help: "En-tête Authorization: Bearer <token>." },
  { value: "header", label: "Clé en en-tête", help: "La clé est envoyée dans un en-tête HTTP (ex. X-API-KEY)." },
  { value: "query", label: "Clé en paramètre", help: "La clé est ajoutée à l'URL (ex. ?api_key=…)." },
  { value: "basic", label: "Basic", help: "Identifiant et mot de passe (HTTP Basic)." },
  { value: "login", label: "Login → token", help: "Un appel d'authentification renvoie un token, utilisé ensuite en Bearer." },
];

const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default defineComponent({
  name: "DeliveryProviderSettings",
  components: { SettingsNav, DeliveryProviderFormModal },
  setup() {
    const { showToast } = useAppToast();
    const providers = ref<Provider[]>([]);
    const loading = ref(true);
    const loadError = ref(false);
    const search = ref("");
    const lastSavedId = ref<number | null>(null);

    const authOf = (v: string) => authOptions.find((o) => o.value === v);
    const authLabel = (v: string) => authOf(v)?.label || v;
    const initials = (name: string) =>
      (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();

    async function fetchProviders() {
      loading.value = true;
      loadError.value = false;
      try {
        const res = await api.get("/api/auth/deliveryProviders");
        providers.value = res.data || [];
      } catch (error) {
        console.error("Error fetching providers:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }
    onMounted(fetchProviders);

    const filtered = computed(() => {
      const q = norm(search.value.trim());
      return q ? providers.value.filter((p) => norm(p.name).includes(q) || norm(p.code || "").includes(q)) : providers.value;
    });

    // ── Create / edit (shared modal) ──
    const formModal = ref<InstanceType<typeof DeliveryProviderFormModal> | null>(null);
    const openForm = (item: Provider | null) => formModal.value?.open(item);
    async function onSaved(provider: Provider | null) {
      lastSavedId.value = provider?.id ?? null;
      await fetchProviders();
    }

    // ── Link / unlink a driver (providers are not created here) ──
    async function linkDriver(item: Provider) {
      let drivers: { id: number; name: string; phone?: string }[] = [];
      try {
        drivers = (await api.get("/api/auth/deliveryProviders/free-drivers")).data || [];
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Impossible de charger les livreurs.", "error");
        return;
      }
      if (!drivers.length) {
        showToast("Tous les livreurs sont déjà liés à un prestataire.", "error");
        return;
      }
      const chosen = await pickWithSelect2({
        title: `Lier « ${item.name} » à un livreur`,
        placeholder: "Rechercher un livreur…",
        options: drivers.map((d) => ({ id: d.id, text: d.phone ? `${d.name} (${d.phone})` : d.name })),
        confirmText: "Lier",
      });
      if (!chosen) return;
      await sendLink(item, Number(chosen), "Prestataire lié au livreur.");
    }

    function unlink(item: Provider) {
      Swal.fire({
        title: "Dissocier du livreur ?",
        text: `« ${item.name} » ne sera plus proposé à l'expédition tant qu'aucun livreur n'y est lié.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, dissocier",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then((result: any) => {
        if (result.isConfirmed) sendLink(item, null, "Prestataire dissocié.");
      });
    }

    async function sendLink(item: Provider, userId: number | null, okMessage: string) {
      try {
        const res = await api.post(`/api/auth/deliveryProviders/${item.id}/link`, { user_id: userId });
        if (res.data?.success) {
          showToast(okMessage, "success");
          lastSavedId.value = item.id;
          await fetchProviders();
        }
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Erreur lors de la liaison.", "error");
      }
    }

    // ── Toggle / delete ──
    async function toggle(item: Provider) {
      try {
        const res = await api.post(`/api/auth/deliveryProviders/${item.id}/toggle`);
        if (res.data?.success) {
          item.active = res.data.active;
          showToast(item.active ? "Prestataire activé." : "Prestataire désactivé.", "success");
        }
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Erreur lors de la mise à jour.", "error");
      }
    }

    function remove(item: Provider) {
      Swal.fire({
        title: "Supprimer ce prestataire ?",
        text: `« ${item.name} » et sa configuration API seront définitivement supprimés.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E44A4A",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        try {
          const res = await api.post(`/api/auth/deliveryProviders/${item.id}/delete`);
          if (res.data?.success) {
            showToast("Prestataire supprimé.", "success");
            fetchProviders();
          }
        } catch (error: any) {
          showToast(error?.response?.data?.message || "Erreur lors de la suppression.", "error");
        }
      });
    }

    return {
      providers,
      loading,
      loadError,
      search,
      filtered,
      lastSavedId,
      authLabel,
      initials,
      fetchProviders,
      formModal,
      openForm,
      onSaved,
      toggle,
      remove,
      linkDriver,
      unlink,
    };
  },
});
</script>

<style scoped>
.dp-head-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.dp-url {
  margin-left: 6px;
  color: var(--ui-text-muted);
  word-break: break-all;
}
.sx-tag.tone-success {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}
.sx-tag.tone-muted {
  color: var(--ui-text-muted);
}
.sx-tag.tone-warning {
  background: var(--ui-warning-soft, #fff4e0);
  color: var(--ui-warning, #b26b00);
}
.sx-tag.tone-driver {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
</style>
